import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface UpdateBrokerageParams {
  brokerageSlug: string;
  field: string;
  value: any;
}

export const useBrokerageEditor = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const updateBrokerage = useMutation({
    mutationFn: async ({ brokerageSlug, field, value }: UpdateBrokerageParams) => {
      const { data, error } = await supabase
        .from('brokerages')
        .update({ 
          [field]: value,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('slug', brokerageSlug)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Brokerage not found');
      return data;
    },
    onSuccess: (data, variables) => {
      // Immediately set the query data to force UI update
      queryClient.setQueryData(['brokerage', variables.brokerageSlug], (oldData: any) => {
        if (oldData) {
          return { ...oldData, [variables.field]: variables.value };
        }
        return oldData;
      });
      
      // Then invalidate and refetch for fresh data from server
      queryClient.invalidateQueries({ queryKey: ['brokerage', variables.brokerageSlug] });
      queryClient.invalidateQueries({ queryKey: ['brokerages'] });
      queryClient.refetchQueries({ queryKey: ['brokerage', variables.brokerageSlug], type: 'active' });
      
      toast.success('Changes saved successfully');
    },
    onError: (error: any) => {
      console.error('Error updating brokerage:', error);
      toast.error('Failed to save changes: ' + error.message);
    },
  });

  const publishBrokerage = useMutation({
    mutationFn: async (brokerageSlug: string) => {
      const { data, error } = await supabase
        .from('brokerages')
        .update({ 
          is_published: true,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('slug', brokerageSlug)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Brokerage not found');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokerages'] });
      toast.success('Brokerage published successfully');
    },
    onError: (error: any) => {
      console.error('Error publishing brokerage:', error);
      toast.error('Failed to publish: ' + error.message);
    },
  });

  const deleteBrokerage = useMutation({
    mutationFn: async (brokerageSlug: string) => {
      // First get the brokerage UUID from slug
      const { data: brokerage } = await supabase
        .from('brokerages')
        .select('id')
        .eq('slug', brokerageSlug)
        .maybeSingle();
      
      if (!brokerage) throw new Error('Brokerage not found');
      
      // Delete related data first (to avoid foreign key violations)
      await supabase.from('brokerage_locations').delete().eq('brokerage_id', brokerage.id);
      await supabase.from('brokerage_leads').delete().eq('brokerage_id', brokerage.id);
      
      // Finally delete the brokerage
      const { error } = await supabase
        .from('brokerages')
        .delete()
        .eq('slug', brokerageSlug);
      
      if (error) throw error;
      return brokerageSlug;
    },
    onSuccess: (brokerageSlug) => {
      queryClient.invalidateQueries({ queryKey: ['brokerages'] });
      queryClient.invalidateQueries({ queryKey: ['brokerage', brokerageSlug] });
      toast.success('Brokerage deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting brokerage:', error);
      toast.error('Failed to delete brokerage: ' + error.message);
    },
  });

  return {
    updateBrokerage: updateBrokerage.mutate,
    publishBrokerage: publishBrokerage.mutate,
    deleteBrokerage: deleteBrokerage.mutate,
    isUpdating: updateBrokerage.isPending,
    isPublishing: publishBrokerage.isPending,
    isDeleting: deleteBrokerage.isPending,
  };
};
